import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { LoggedInUserStore } from '@logged-in-user-store';
import { LocalStorageKeys } from '@shared-utils';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';

export const AuthGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const loggedInUserStore = inject(LoggedInUserStore);

  // in case of '/' route, avoid running the guard
  return state.url === '/' ? of(true) : isAlreadyAuthenticated(router, loggedInUserStore);
};

const isAlreadyAuthenticated = (
  router: Router,
  loggedInUserStore: InstanceType<typeof LoggedInUserStore>,
): Observable<boolean> => {
  const token = localStorage.getItem(LocalStorageKeys.TOKEN);

  if (!token) {
    loggedInUserStore.logout();
    router.navigate(['/login']);
    return of(false);
  }

  // Check if profile is already loaded
  if (loggedInUserStore.user()) {
    // check if it's a temporary user (questionnaire prelogin token/user in Clinic)
    return loggedInUserStore.user()?.type?.temporary ? of(false) : of(true);
  }

  // Fetch profile if it hasn't been retrieved yet
  loggedInUserStore.setToken(token);
  return loggedInUserStore.hasFinishedLoadingUserDetailsApi$.pipe(
    switchMap(userDetailsApiStatus =>
      userDetailsApiStatus === 'error'
        ? throwError(() => loggedInUserStore.loadUserDetailsResource.error())
        : loggedInUserStore.user()?.type?.temporary
          ? of(false)
          : of(true),
    ),
    catchError(() => {
      router.navigate(['/login']);
      return of(false);
    }),
  );
};
